/** Something about a coupon or a site that needs a person, raised onto the Work Pipeline. */
export enum CouponIssueType {
  NeedsSuspension = "needs-suspension",
  NeedsActivation = "needs-activation",
  FailingForShoppers = "failing-for-shoppers",
  CouponBoxNotFound = "coupon-box-not-found",
}

export enum CouponIssueStatus {
  Open = "open",
  Resolved = "resolved",
  Dismissed = "dismissed",
}

export const COUPON_ISSUE_LABELS: Record<CouponIssueType, string> = {
  [CouponIssueType.NeedsSuspension]: "Needs suspension",
  [CouponIssueType.NeedsActivation]: "Needs activation",
  [CouponIssueType.FailingForShoppers]: "Failing for shoppers",
  [CouponIssueType.CouponBoxNotFound]: "Coupon box not found",
};

export const COUPON_ISSUE_EXPLAINERS: Record<CouponIssueType, string> = {
  [CouponIssueType.NeedsSuspension]:
    "Live code that the merchant rejected in our coupon test. Check it, then suspend it.",
  [CouponIssueType.NeedsActivation]:
    "Code is switched off but our coupon test did not reject it. Check it, then re-enable it.",
  [CouponIssueType.FailingForShoppers]:
    "Live code that real shoppers tried and that never applied. Check it, then suspend it.",
  [CouponIssueType.CouponBoxNotFound]:
    "Shoppers got \"Coupon box not found\": our template could not find where to enter the code.",
};

/** Issues a tester clears by changing the code itself. */
export const COUPON_CODE_ISSUE_TYPES: CouponIssueType[] = [
  CouponIssueType.NeedsSuspension,
  CouponIssueType.NeedsActivation,
  CouponIssueType.FailingForShoppers,
];

/** Where a coupon's domain was found, most specific first. */
export type TCouponDomainSource =
  | "merchant"
  | "checkout-url"
  | "coupon"
  | "deal"
  | "brand";

export const COUPON_DOMAIN_SOURCE_LABELS: Record<TCouponDomainSource, string> =
  {
    merchant: "test merchant",
    "checkout-url": "checkout URL",
    coupon: "coupon domains",
    deal: "deal domains",
    brand: "brand domain",
  };

/**
 * The Coupon Results "Needs Suspension" / "Needs Activation" filters. Shared so
 * the buttons and the daily feed always mean the same thing.
 */
export const COUPON_RESULT_PRESETS = {
  needsSuspension: {
    publishedStatus: [2],
    confirmedFailure: true,
    applied: false,
    systemError: false,
    notApplicable: false,
  },
  needsActivation: {
    publishedStatus: [0, 1, 3],
    confirmedFailure: false,
    systemError: false,
  },
} as const;

export interface ICouponIssue {
  _id: string;
  type: CouponIssueType;
  status: CouponIssueStatus;
  domain: string;
  countryCode: string;
  domainSource: TCouponDomainSource;
  /** Empty for site-level issues (coupon box not found). */
  code: string;
  brandId: string | null;
  brandName: string | null;
  couponId: string | null;
  attempts: number;
  failures: number;
  lastError: string | null;
  /** Up to a few codes the evidence came from, for site-level issues. */
  sampleCodes: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolvedReason: string | null;
  dismissedAt: string | null;
  dismissedByName: string | null;
  dismissReason: string | null;
}

/** Open issue counts per type for one domain + geo. */
export type TCouponIssueCounts = Record<CouponIssueType, number>;

export interface ICouponIssueFeedSummary {
  dryRun: boolean;
  window: { from: string; to: string };
  startedAt: string;
  finishedAt: string | null;
  /** Issues found in the window, per type. */
  found: TCouponIssueCounts;
  /** Of those: brand new, reopened after being resolved, or already open. */
  created: number;
  reopened: number;
  alreadyOpen: number;
  /** Matched an issue someone marked "Not an issue" — left alone. */
  dismissed: number;
  /** Open issues closed because the code's status changed. */
  resolved: number;
  /** Live codes we could not tie to one domain and geo, so nothing was raised. */
  noDomain: number;
  noGeo: number;
  /** Domain + geo pairs with open issues: already on the board vs added to it. */
  boardRowsExisting: number;
  boardRowsAdded: number;
  error: string | null;
}
